import { Spin } from "antd";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Col, Form, Row } from "react-bootstrap";
import { Field, FormikProvider, useFormik } from "formik";
import CustomInput from "../../../CustomControls/CustomInput";
import CustomSelect from "../../../CustomControls/CustomSelect";
import CustomFlatpickr from "../../../CustomControls/CustomFlatpickr";
import CustomSizePerPage from "../../../CustomControls/CustomSizePerPage";
import CustomPaginationButtons from "../../../CustomControls/CustomPaginationButtons";
import { pifGet, pifPost } from "../../../helpers/api_helper";
import {
  GET_PIF_SyncQueue,
  POST_PIF_RetryJob,
  POST_PIF_RetryAllJobs,
} from "../../../helpers/url_helper";
import {
  PIF_QUEUE_STATUS,
  PIF_JOB_TYPE,
  PIF_FAILOUT_REASON,
  pifDateTime,
} from "./pifConstants";
import {
  successMessage,
  errorMessage,
  customConfirmDialog,
} from "../CommonFunctions";

const fmt = pifDateTime;

// Local wall-clock stamp "YYYY-MM-DDTHH:mm:ss" for the date-range filter (the proc
// now compares against server-local CreatedOn, so don't send a UTC ISO string).
const localStamp = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
    `T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  );
};

const statusOptions = [
  { label: "All", value: -1 },
  { label: "Ready", value: 1 },
  { label: "In Progress", value: 2 },
  { label: "Synced", value: 3 },
  { label: "Blocked", value: 4 },
  { label: "Dead", value: 5 },
];

const jobTypeOptions = [
  { label: "All", value: -1 },
  { label: "Invoice", value: 1 },
  { label: "Master Push", value: 2 },
  { label: "Master Validation", value: 3 },
  { label: "Invoice Cancel", value: 4 },
];

const SyncQueue = () => {
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const companyIdEnc = AuthUser?.CompanyIDEncrypt;
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const isFilterExpanded =
    JSON.parse(localStorage.getItem("userDetails"))?.IsFilterExpanded === true;
  const [showFilter, setShowFilter] = useState(isFilterExpanded);
  const [rows, setRows] = useState([]);
  const [recordCount, setRecordCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(10);

  const initialStatus = Number(searchParams.get("status")) || -1;

  const formikFilter = useFormik({
    initialValues: {
      Search: "",
      Status: statusOptions.some((o) => o.value === initialStatus)
        ? initialStatus
        : -1,
      JobType: -1,
      FromDate: "",
      ToDate: "",
    },
    onSubmit: () => getData(1, currentSizePerPage),
    validateOnBlur: false,
  });

  const {
    values: { Search, Status, JobType, FromDate, ToDate },
  } = formikFilter;

  const getData = async (page = 1, sizePerPage = 10) => {
    setIsLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("companyIdEnc", companyIdEnc);
      p.set("pageNo", page);
      p.set("pageSize", sizePerPage);
      if (Status > 0) p.set("status", Status);
      if (JobType > 0) p.set("jobType", JobType);
      if (Search) p.set("search", Search);
      if (FromDate) {
        const f = new Date(FromDate);
        f.setHours(0, 0, 0, 0);
        p.set("fromUtc", localStamp(f));
      }
      if (ToDate) {
        const t = new Date(ToDate);
        t.setHours(0, 0, 0, 0);
        t.setDate(t.getDate() + 1);
        p.set("toUtc", localStamp(t));
      }

      const res = await pifGet(`${GET_PIF_SyncQueue}?${p.toString()}`);
      const items = res?.Items || [];
      setRows(items);
      setRecordCount(res?.TotalRows || 0);
      if (page > 1 && items.length === 0) {
        setCurrentPage(page - 1);
        getData(page - 1, sizePerPage);
      } else {
        setCurrentPage(page);
      }
      setCurrentSizePerPage(sizePerPage);
    } catch (e) {
      errorMessage("Could not load the sync queue. The service may be offline.");
      setRows([]);
      setRecordCount(0);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getData(1, currentSizePerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Status, JobType]);

  const retryOne = (queueId, invoiceNo) => {
    customConfirmDialog(
      "Retry job",
      `Re-queue ${invoiceNo || "this job"} for another sync attempt?`,
      async () => {
        setIsLoading(true);
        try {
          const res = await pifPost(POST_PIF_RetryJob, {
            CompanyIdEnc: companyIdEnc,
            QueueId: queueId,
          });
          res?.IsSuccess
            ? successMessage(res?.Message || "Re-queued.")
            : errorMessage(res?.Message || "Could not retry.");
        } catch {
          errorMessage("Could not retry the job.");
        }
        getData(currentPage, currentSizePerPage);
      },
      true
    );
  };

  const retryAll = (status, label) => {
    customConfirmDialog(
      "Retry all",
      `Re-queue every ${label} job for this company?`,
      async () => {
        setIsLoading(true);
        try {
          const res = await pifPost(POST_PIF_RetryAllJobs, {
            CompanyIdEnc: companyIdEnc,
            Status: status,
          });
          res?.IsSuccess
            ? successMessage(res?.Message || "Re-queued.")
            : errorMessage(res?.Message || "Could not retry.");
        } catch {
          errorMessage("Could not retry the jobs.");
        }
        getData(1, currentSizePerPage);
      },
      true
    );
  };

  return (
    <div className="card m-1">
      <div className="card-body p-1">
        <Spin size="large" spinning={isLoading} tip={"Loading..."} style={{ position: "fixed" }}>
          <div className="table-responsive active-projects style-1 shorting">
            <div className="tbl-caption p-2 d-flex justify-content-between align-items-center">
              <h4 className="heading mb-0">Sync Queue</h4>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-outline-primary btn-sm me-2"
                  onClick={() => getData(currentPage, currentSizePerPage)}
                >
                  <i className="fa-solid fa-refresh me-2" /> Reload
                </button>
                <button
                  className="btn btn-outline-primary btn-sm me-2"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  <i className="fa-solid fa-filter me-2" /> Filter
                </button>
                <button
                  className="btn btn-outline-danger btn-sm me-2"
                  onClick={() => retryAll(4, "blocked")}
                >
                  Retry all blocked
                </button>
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => retryAll(5, "dead")}
                >
                  Retry all dead
                </button>
              </div>
            </div>
            <hr className="mt-0 mb-1" />

            {showFilter && (
              <div className="p-2 pt-0 pb-0">
                <FormikProvider value={formikFilter}>
                  <Form onSubmit={formikFilter.handleSubmit}>
                    <Row className="me-0">
                      <Col lg={3}>
                        <Field
                          label="Invoice No"
                          name="Search"
                          placeholder="Search invoice no..."
                          component={CustomInput}
                          autoFocus={isFilterExpanded}
                        />
                      </Col>
                      <Col lg={2}>
                        <Field
                          label="Status"
                          name="Status"
                          options={statusOptions}
                          component={CustomSelect}
                          isClearable={false}
                          menuPosition="fixed"
                        />
                      </Col>
                      <Col lg={2}>
                        <Field
                          label="Job Type"
                          name="JobType"
                          options={jobTypeOptions}
                          component={CustomSelect}
                          isClearable={false}
                          menuPosition="fixed"
                        />
                      </Col>
                      <Col lg={3}>
                        <label className="mb-1">Created</label>
                        <div className="d-flex">
                          <Field
                            name="FromDate"
                            placeholder={"From Date"}
                            component={CustomFlatpickr}
                            options={{
                              altInput: true,
                              altFormat: "j-M-Y",
                              dateFormat: "d-m-Y",
                            }}
                          />
                          <div>&nbsp;</div>
                          <Field
                            name="ToDate"
                            placeholder={"To Date"}
                            component={CustomFlatpickr}
                            options={{
                              altInput: true,
                              altFormat: "j-M-Y",
                              dateFormat: "d-m-Y",
                              minDate: FromDate,
                            }}
                          />
                        </div>
                      </Col>
                      <Col lg={2}>
                        <label className="mb-1">&nbsp;</label>
                        <div className="d-flex">
                          <div className="d-lg-block">
                            <Link
                              className="btn btn-primary btn-sm"
                              onClick={() => getData(1, currentSizePerPage)}
                            >
                              <i className="fa fa-search" aria-hidden="true"></i> Search
                            </Link>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                </FormikProvider>
              </div>
            )}

            <div className="dataTables_wrapper no-footer">
              <table className="table dataTable no-footer mb-0">
                <thead>
                  <tr>
                    <th width="8%"></th>
                    <th>Invoice</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Attempts</th>
                    <th>Detail</th>
                    <th>Created</th>
                    <th>Next attempt</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length > 0 ? (
                    rows.map((r) => {
                      const s = PIF_QUEUE_STATUS[r.Status] || { label: r.Status, badge: "secondary" };
                      const canRetry = r.Status === 4 || r.Status === 5;
                      return (
                        <tr key={r.QueueId}>
                          <td>
                            <Link
                              to={`/sync-detail/${window.btoa(String(r.QueueId))}`}
                              className="icon-box icon-box-md me-1"
                              title="View detail"
                            >
                              <i className="fa fa-eye text-primary" />
                            </Link>
                            {canRetry && (
                              <Link
                                to="#"
                                className="icon-box icon-box-md"
                                title="Retry"
                                onClick={() => retryOne(r.QueueId, r.InvoiceNo)}
                              >
                                <i className="fa fa-redo text-warning" />
                              </Link>
                            )}
                          </td>
                          <td>
                            {r.InvoiceNo || (r.JobType === 2 ? "(masters)" : `#${r.QueueId}`)}
                          </td>
                          <td>{PIF_JOB_TYPE[r.JobType] || r.JobType}</td>
                          <td>
                            <span className={`badge badge-${s.badge} light`}>{s.label}</span>
                          </td>
                          <td>
                            {r.AttemptCount}/{r.MaxAttempts}
                          </td>
                          <td style={{ maxWidth: 320 }} className="text-truncate" title={r.LastError || ""}>
                            {PIF_FAILOUT_REASON[r.LastFailoutReason] || r.LastError || "—"}
                          </td>
                          <td>{fmt(r.CreatedOn)}</td>
                          <td>{r.Status === 1 ? fmt(r.NextAttemptAtUtc) : "—"}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8}>No jobs in the queue.</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="d-sm-flex text-center justify-content-between align-items-center">
                <CustomSizePerPage getData={getData} currentSizePerPage={currentSizePerPage} />
                <div className="dataTables_info">
                  Showing{" "}
                  {recordCount > 0 ? (currentPage - 1) * currentSizePerPage + 1 : 0} to{" "}
                  {recordCount > currentPage * currentSizePerPage
                    ? currentPage * currentSizePerPage
                    : recordCount}{" "}
                  of {recordCount} entries
                </div>
                <CustomPaginationButtons
                  totalPages={recordCount / currentSizePerPage}
                  currentPage={currentPage}
                  currentSizePerPage={currentSizePerPage}
                  getData={getData}
                />
              </div>
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default SyncQueue;
